import { useState, useCallback } from 'preact/hooks'
import { useWorkspaceCapabilities } from '@/hooks/useWorkspaceCapabilities'
import { Pencil, Plus, X, GripVertical, Check, Mic, AlertTriangle } from 'lucide-preact'
import type { OnlineEventSpeakerViewDto, WebSpeakerPatchItemDto } from '@/api/webs'
import { PersonAvatar } from '@/components/common/Avatar'
import { SpeakerCard } from './SpeakerCard'

interface SpeakerUi extends OnlineEventSpeakerViewDto {}
interface SpeakerUiWithPic extends SpeakerUi { ProfilePicUrl?: string | null }

interface SpeakerListSectionProps {
  speakers: SpeakerUiWithPic[]
  isSignedOff?: boolean
  syncing?: boolean
  speakersSyncReviewNeeded?: boolean
  onAddParticipant: () => void
  onEditSpeaker: (sp: OnlineEventSpeakerViewDto) => void
  onDeleteSpeaker: (sp: SpeakerUi) => void
  onSaveReorder: (reordered: WebSpeakerPatchItemDto[]) => Promise<boolean>
}

export function SpeakerListSection({
  speakers,
  isSignedOff,
  syncing,
  speakersSyncReviewNeeded,
  onAddParticipant,
  onEditSpeaker,
  onDeleteSpeaker,
  onSaveReorder,
}: SpeakerListSectionProps) {
  const [editingSpeakers, setEditingSpeakers] = useState(false)
  const [reorderList, setReorderList] = useState<SpeakerUi[]>([])
  const [savingReorder, setSavingReorder] = useState(false)
  const [speakerDragIndex, setSpeakerDragIndex] = useState<number | null>(null)
  const [speakerDragOverIndex, setSpeakerDragOverIndex] = useState<number | null>(null)

  const speakerOrderChanged = editingSpeakers && reorderList.length === speakers.length && reorderList.some((s, i) => s.ContactId !== (speakers[i]?.ContactId))

  const beginEditSpeakers = () => { setReorderList([...(speakers as SpeakerUi[])]); setEditingSpeakers(true) }
  const cancelEditSpeakers = () => { setEditingSpeakers(false); setReorderList([]); setSpeakerDragIndex(null); setSpeakerDragOverIndex(null) }

  const moveReorder = useCallback((from: number, to: number) => {
    setReorderList(prev => { const copy = [...prev]; const item = copy.splice(from, 1)[0]; copy.splice(to, 0, item); return copy })
  }, [])

  const saveReorder = async () => {
    setSavingReorder(true)
    const patchItems: WebSpeakerPatchItemDto[] = reorderList.map(s => ({
      ContactId: s.ContactId!, Name: s.Name, Email: s.Email, Title: s.Title,
      CompanyName: s.CompanyName, CompanyId: s.CompanyId || undefined,
      Bio: s.Bio || undefined, SpeakerType: s.SpeakerType,
    }))
    const ok = await onSaveReorder(patchItems)
    if (ok) { setEditingSpeakers(false); setReorderList([]) }
    setSavingReorder(false)
  }

  const { canEdit } = useWorkspaceCapabilities()
  const disabled = isSignedOff || syncing

  const handleDelete = useCallback((sp: SpeakerUi) => {
    if (!isSignedOff && !syncing) onDeleteSpeaker(sp)
  }, [isSignedOff, syncing, onDeleteSpeaker])

  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5 shadow-sm">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-900 dark:text-white flex items-center gap-2"><Mic className="w-5 h-5 text-primary-600" /> Speakers</h2>
        {!editingSpeakers ? (
          canEdit && (
            <div className="flex items-center gap-2">
              <button type="button" onClick={beginEditSpeakers} disabled={disabled || speakers.length < 2} className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed"><Pencil className="w-4 h-4" /> Edit Order</button>
              <button type="button" onClick={onAddParticipant} disabled={disabled} className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed"><Plus className="w-4 h-4" /> Add Participant</button>
            </div>
          )
        ) : (
          <div className="flex items-center gap-2">
            {canEdit && <button type="button" onClick={saveReorder} disabled={savingReorder || !speakerOrderChanged} className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50">{savingReorder ? 'Saving...' : (<><Check className="w-4 h-4" /> Save</>)}</button>}
            <button type="button" onClick={cancelEditSpeakers} disabled={savingReorder} className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"><X className="w-4 h-4" /> Cancel</button>
          </div>
        )}
      </div>
      {speakersSyncReviewNeeded && (
        <div className="mt-3 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-3 flex items-center gap-3 text-sm text-amber-800 dark:text-amber-300">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <span>Some BigMarker presenters could not be matched to contacts. Review the speaker list.</span>
        </div>
      )}
      {!editingSpeakers && (
        <div className="mt-4 space-y-4">
          {speakers.length === 0 && <p className="text-slate-500 dark:text-slate-400 text-sm">No speakers yet.</p>}
          {speakers.map((speaker, i) => (
            <SpeakerCard key={speaker.ContactId || i} speaker={speaker} onDelete={disabled ? undefined : handleDelete} onEdit={disabled ? undefined : () => onEditSpeaker(speaker)} />
          ))}
        </div>
      )}
      {editingSpeakers && (
        <div className="mt-4 space-y-2">
          {reorderList.length === 0 && <p className="text-slate-500 dark:text-slate-400 text-sm">No speakers.</p>}
          {reorderList.map((speaker, i) => {
            const name = speaker.Name || 'Speaker'
            const avatar = (speaker as SpeakerUiWithPic).ProfilePicUrl || null
            const email = speaker.Email || ''
            const isDragOver = speakerDragOverIndex === i && speakerDragIndex !== null && speakerDragIndex !== i
            return (
              <div key={speaker.ContactId || i} className={`flex items-center gap-3 p-2 rounded-md border transition-colors ${isDragOver ? 'border-primary-400 bg-primary-50 dark:border-primary-500 dark:bg-primary-900/20' : 'border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900'}`} draggable onDragStart={(e) => { setSpeakerDragIndex(i); if (e.dataTransfer) { e.dataTransfer.effectAllowed = 'move' } }} onDragOver={(e) => { e.preventDefault(); if (speakerDragOverIndex !== i) setSpeakerDragOverIndex(i) }} onDragLeave={() => { if (speakerDragOverIndex === i) setSpeakerDragOverIndex(null) }} onDrop={(e) => { e.preventDefault(); if (speakerDragIndex !== null && speakerDragIndex !== i) moveReorder(speakerDragIndex, i); setSpeakerDragIndex(null); setSpeakerDragOverIndex(null) }} onDragEnd={() => { setSpeakerDragIndex(null); setSpeakerDragOverIndex(null) }}>
                    <button type="button" className="cursor-grab p-1.5 rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-500 dark:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500" tabIndex={0} aria-label={`Reorder speaker ${i + 1}. Use Arrow Up / Arrow Down to move.`} onKeyDown={(e) => { if (e.key === 'ArrowUp' && i > 0) { e.preventDefault(); moveReorder(i, i - 1) } else if (e.key === 'ArrowDown' && i < reorderList.length - 1) { e.preventDefault(); moveReorder(i, i + 1) } }}><GripVertical className="w-5 h-5" /></button>
                    <PersonAvatar fullName={name} profilePicUrl={avatar} size="md" />
                    <div className="flex-1 min-w-0"><div className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate">{name}</div><div className="text-[11px] text-slate-500 dark:text-slate-400 truncate">{email}</div></div>
                    <div className="w-8 text-center text-[11px] text-slate-500 dark:text-slate-400 select-none">{i + 1}</div>
              </div>
            )
          })}
          <p className="text-[11px] text-slate-500 dark:text-slate-400">Drag speakers or focus a handle and press Arrow Up/Down to reorder.</p>
        </div>
      )}
    </div>
  )
}
